// src/auth/SubscribeGate.jsx
// Wraps a premium feature. Free tenants pass through untouched; subscription
// tenants require a signed-in, subscribed (or trialing) user.
import React, { useState } from "react";
import { Lock } from "lucide-react";
import { useAuth } from "./AuthContext";
import { useSubscription } from "./useSubscription";
import "./SubscribeGate.css";

function SubscribeGate({ premiumGate, featureName = "This feature", children }) {
  const { user, loading: authLoading, openLogin } = useAuth();
  const {
    loading: subLoading,
    status,
    isSubscriber,
    startCheckout,
  } = useSubscription();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Dev bypass: `localStorage.setItem('bw:devPremium','1')` in the console
  // unlocks premium UI locally (RLS still applies to gated data reads).
  // NODE_ENV check makes this dead code in production builds.
  const devBypass =
    process.env.NODE_ENV === "development" &&
    typeof window !== "undefined" &&
    window.localStorage.getItem("bw:devPremium") === "1";

  // Feature is free for this tenant.
  if (premiumGate !== "subscription" || devBypass) {
    return <>{children}</>;
  }

  if (authLoading || subLoading) {
    return (
      <div className="sg-loading" role="status" aria-label="Loading">
        <span className="sg-spinner" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="sg-card">
        <div className="sg-icon">
          <Lock size={22} strokeWidth={2} aria-hidden="true" />
        </div>
        <h3 className="sg-heading">{featureName} is a subscriber feature</h3>
        <p className="sg-copy">Sign in to start your 3-day free trial.</p>
        <button type="button" className="sg-button" onClick={openLogin}>
          Sign in
        </button>
      </div>
    );
  }

  if (!isSubscriber) {
    const handleCheckout = async () => {
      setBusy(true);
      setError("");
      const { error: checkoutError } = await startCheckout();
      if (checkoutError) {
        setError(checkoutError);
        setBusy(false);
      }
      // On success the browser navigates to Stripe Checkout.
    };

    return (
      <div className="sg-card">
        <div className="sg-icon">
          <Lock size={22} strokeWidth={2} aria-hidden="true" />
        </div>
        <h3 className="sg-heading">Start your free trial</h3>
        <p className="sg-copy">
          3 days free, cancel anytime. Then it converts to a paid monthly
          subscription.
        </p>
        {error && <p className="sg-error">{error}</p>}
        <button
          type="button"
          className="sg-button"
          onClick={handleCheckout}
          disabled={busy}
        >
          {busy
            ? "Redirecting…"
            : status === "canceled"
            ? "Resubscribe"
            : "Start 3-day free trial"}
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

export default SubscribeGate;
